import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { Button, Offcanvas, OffcanvasBody, OffcanvasHeader, Spinner } from 'reactstrap';
import FullCalendar from '@fullcalendar/react';
import BootstrapTheme from '@fullcalendar/bootstrap';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import ruLocale from '@fullcalendar/core/locales/ru';
import uaLocale from '@fullcalendar/core/locales/uk';
import { toast } from 'react-toastify';
import { MODALS } from '@/configs/modals';
import { getDate, normalizeSchedulers } from '@/utils/helpers';
import { fetchUser, fetchUserApi } from '@/store/user/actions';
import { fetchPricesApi, fetchNutritionServicesApi } from '@/store/prices/actions';
import { fetchPsychologistsApi } from '@/store/psychologists/actions';
import { fetchPsychologistSchedulerApi } from '@/store/psychologist/actions';
import { submitConsultation } from '@/store/consultations/consultationsActions';
import { fetchNutritionists, fetchNutritionistSchedulersApi, submitNutritionConsultation } from '@/store/nutrition/actions';
import { ListItem, ListItems } from '@/components/ListItems';

const PSYCHOLOGIST = 'psychologist';
const NUTRITIONIST = 'nutritionist';
const getItems = (response) => Array.isArray(response) ? response : response?.results || [];
const getId = (item) => item?.id || item;

const UserConsultationBookingDrawer = ({ user, onClose }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [specialistType, setSpecialistType] = useState(null);
  const [consultationType, setConsultationType] = useState(null);
  const [specialist, setSpecialist] = useState(null);
  const [slot, setSlot] = useState(null);
  const [client, setClient] = useState(null);
  const [catalog, setCatalog] = useState({ services: [], specialists: [] });
  const [slots, setSlots] = useState([]);
  const [loadingClient, setLoadingClient] = useState(true);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [clientError, setClientError] = useState(false);
  const [catalogError, setCatalogError] = useState(false);
  const [slotsError, setSlotsError] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reload, setReload] = useState(0);
  const mounted = useRef(false);
  const type = specialistType?.value;
  const specialistId = specialist?.value;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let active = true;
    setLoadingClient(true);
    setClientError(false);
    fetchUserApi(user.id)
      .then((data) => { if (active) setClient(data); })
      .catch(() => { if (active) setClientError(true); })
      .finally(() => { if (active) setLoadingClient(false); });
    return () => { active = false; };
  }, [user.id, reload]);

  useEffect(() => {
    if (!type) return;
    let active = true;
    setLoadingCatalog(true);
    setCatalogError(false);
    const requests = type === NUTRITIONIST
      ? [fetchNutritionServicesApi(), fetchNutritionists().payload]
      : [fetchPricesApi(), fetchPsychologistsApi()];
    Promise.all(requests)
      .then(([services, specialists]) => {
        if (active) setCatalog({ services: getItems(services), specialists: getItems(specialists) });
      })
      .catch(() => { if (active) setCatalogError(true); })
      .finally(() => { if (active) setLoadingCatalog(false); });
    return () => { active = false; };
  }, [type, reload]);

  useEffect(() => {
    if (!specialistId) return;
    let active = true;
    setLoadingSlots(true);
    setSlotsError(false);
    const fetchSlots = type === NUTRITIONIST ? fetchNutritionistSchedulersApi : fetchPsychologistSchedulerApi;
    fetchSlots(specialistId)
      .then((data) => {
        if (active) setSlots((data?.schedulers || []).filter((item) => !item.taken));
      })
      .catch(() => { if (active) setSlotsError(true); })
      .finally(() => { if (active) setLoadingSlots(false); });
    return () => { active = false; };
  }, [type, specialistId, reload]);

  const typeOptions = useMemo(() => {
    const consultations = (type === NUTRITIONIST ? client?.nutrition_consultations : client?.consultations) || [];
    const counts = consultations.reduce((result, item) => {
      const serviceType = item.service?.type || item.service_type;
      const assignedSpecialist = type === NUTRITIONIST ? item.nutritionist : item.psychologist;
      const unexpired = !item.expires_at || new Date(item.expires_at).getTime() >= Date.now();
      if (item.status === 'bought' && !getId(item.schedule) && !getId(assignedSpecialist) && unexpired && serviceType) {
        result[serviceType] = (result[serviceType] || 0) + 1;
      }
      return result;
    }, {});
    return [...new Set(catalog.services.map((service) => service.type).filter(Boolean))].map((value) => ({
      value,
      label: t(`${type === NUTRITIONIST ? 'nutrition.service_types' : 'consultations.types'}.${value}`, { defaultValue: value }),
      count: counts[value] || 0,
      isDisabled: !counts[value],
    }));
  }, [catalog.services, client, type, t]);

  const specialistOptions = catalog.specialists.map((item) => ({
    value: item.id,
    label: item.fullname || item.fullname_uk || item.fullname_ru || `#${item.id}`,
  }));
  const events = slots.map((item) => normalizeSchedulers({ ...item, taken: String(item.id) === String(slot?.id) }));
  const hasAvailableType = typeOptions.some((option) => !option.isDisabled);
  const loadError = clientError || catalogError || slotsError;
  const busy = loadingClient || loadingCatalog || loadingSlots;
  const canSubmit = user?.id && type && consultationType && specialistId && slot && !busy && !loadError &&
    typeOptions.some((option) => option.value === consultationType.value && !option.isDisabled);

  const resetSpecialist = () => {
    setSpecialist(null);
    setSlot(null);
    setSlots([]);
    setLoadingSlots(false);
    setSlotsError(false);
    setSubmitError('');
  };
  const changeSpecialistType = (option) => {
    setSpecialistType(option);
    setConsultationType(null);
    setCatalog({ services: [], specialists: [] });
    setLoadingCatalog(Boolean(option));
    setCatalogError(false);
    resetSpecialist();
  };
  const changeConsultationType = (option) => {
    setConsultationType(option);
    resetSpecialist();
  };
  const retry = () => {
    setConsultationType(null);
    resetSpecialist();
    setReload((value) => value + 1);
  };

  const submit = async () => {
    if (!canSubmit || isSubmitting) return;
    setIsSubmitting(true);
    setSubmitError('');
    const save = type === NUTRITIONIST ? submitNutritionConsultation : submitConsultation;
    let succeeded = false;
    try {
      await save({
        data: { user: user.id, services_type: consultationType.value, scheduler: slot.id },
        dispatch,
        onSuccess: () => { succeeded = true; },
        onError: (error) => {
          if (mounted.current) {
            const errors = error?.errors || error?.message;
            const message = typeof errors === 'string' ? errors : errors ? Object.values(errors).flat().join(' ') : '';
            setSubmitError(message || t('consultation.modal.update_error'));
          }
        },
      });
      if (succeeded && mounted.current) {
        dispatch(fetchUser(user.id));
        toast.success(t('consultation.modal.create_success'));
        onClose();
      }
    } catch (error) {
      if (mounted.current) setSubmitError(t('consultation.modal.update_error'));
    } finally {
      if (mounted.current) setIsSubmitting(false);
    }
  };
  const close = () => { if (!isSubmitting) onClose(); };

  return <Offcanvas isOpen toggle={close} direction="end" className={MODALS.consultation}>
    <OffcanvasHeader toggle={close}>{t('consultation.modal.add_title')}</OffcanvasHeader>
    <OffcanvasBody>
      <ListItems layout="horizontal" className="modal-consultation-resume mb-4">
        <ListItem label={t('consultation.modal.client')} value={user.fullname || user.fullname_uk || user.fullname_ru || user.username || `#${user.id}`} />
        <ListItem label={t('scheduler.modal.specialist_type')} value={<Select
          aria-label={t('scheduler.modal.specialist_type')}
          placeholder={t('scheduler.select_specialist_type')}
          options={[PSYCHOLOGIST, NUTRITIONIST].map((value) => ({ value, label: t(`scheduler.specialist_types.${value}`) }))}
          value={specialistType} onChange={changeSpecialistType} isDisabled={isSubmitting}
          className="select2-selection" classNamePrefix="select2-selection" noOptionsMessage={() => t('no_data')}
        />} />
        <ListItem label={t('consultation.modal.type')} value={<Select
          aria-label={t('consultation.modal.type')} placeholder={t('consultation.modal.select_type')}
          options={typeOptions} value={consultationType} onChange={changeConsultationType}
          isDisabled={!type || loadingClient || loadingCatalog || clientError || catalogError || isSubmitting}
          isLoading={loadingClient || loadingCatalog} className="select2-selection" classNamePrefix="select2-selection"
          noOptionsMessage={() => t('no_data')}
          formatOptionLabel={(option) => <>{option.label}<span className="select-option-badge">{option.count}</span></>}
        />} />
        <ListItem label={type ? t(`scheduler.specialist_types.${type}`) : t('scheduler.modal.specialist')} value={<Select
          aria-label={t('scheduler.modal.specialist')} placeholder={t('scheduler.modal.specialist')}
          options={specialistOptions} value={specialist}
          onChange={(option) => { resetSpecialist(); setSpecialist(option); setLoadingSlots(Boolean(option)); }}
          isDisabled={!consultationType || loadingCatalog || loadError || isSubmitting}
          className="select2-selection" classNamePrefix="select2-selection" noOptionsMessage={() => t('no_data')}
        />} />
        <ListItem label={t('consultation.modal.date')} value={slot
          ? `${getDate(slot.start_datetime, 'DD.MM.YYYY')}, ${getDate(slot.start_datetime, 'HH:mm')}-${getDate(slot.end_datetime, 'HH:mm')}`
          : '-'} />
      </ListItems>
      {loadError && <div className="alert alert-danger" role="alert">
        {t('consultation_booking.load_error')}
        <Button color="link" onClick={retry}>{t('consultation_booking.retry')}</Button>
      </div>}
      {type && !loadingClient && !loadingCatalog && !loadError && !hasAvailableType &&
        <p role="status">{t('consultation_booking.no_balance')}</p>}
      {busy && <Spinner color="primary" />}
      {specialist && !loadingSlots && !slotsError && !slots.length &&
        <p role="status">{t('consultation_booking.no_slots')}</p>}
      {!!slots.length && !loadingSlots && <div className="calendar-scheduler">
        <FullCalendar
          plugins={[BootstrapTheme, dayGridPlugin, interactionPlugin]} themeSystem="bootstrap"
          initialDate={getDate(new Date(), 'YYYY-MM-DD')}
          headerToolbar={{ left: 'prev,next', center: 'title', right: 'today' }}
          events={events} locale={i18n.language === 'ru' ? ruLocale : uaLocale}
          eventTimeFormat={{ hour: '2-digit', minute: '2-digit' }}
          eventContent={({ event }) => <span>{getDate(event.start, 'HH:mm')}-{getDate(event.end, 'HH:mm')}</span>}
          eventClick={({ event }) => {
            if (!isSubmitting) {
              setSlot(slots.find((item) => String(item.id) === String(event.id)) || null);
              setSubmitError('');
            }
          }}
        />
      </div>}
      {submitError && <div className="alert alert-danger mt-3" role="alert">{submitError}</div>}
      <div className="modal-consultation-footer d-flex justify-content-end pt-3">
        <Button color="primary" onClick={submit} disabled={!canSubmit || isSubmitting}>
          {isSubmitting && <Spinner size="sm" className="me-2" />}{t('consultation.modal.btn_save')}
        </Button>
      </div>
    </OffcanvasBody>
  </Offcanvas>;
};

export default UserConsultationBookingDrawer;
